import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors } from '@karnott/colors';
import { AreaIcon, ChevronIcon } from '@karnott/icons';
import { pixelSize, pixelSpacing, zIndex } from '@karnott/theme';
import { Slider, SliderValue } from '../slider';

const AreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  position: relative;
  z-index: ${zIndex('modal')};
`;

const SliderContainer = styled.div<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? `flex` : `none`)};
  padding: ${pixelSpacing('regular')};
  padding-bottom: ${pixelSpacing('regularge')};
  background: ${colors('white', 500)};
  box-sizing: border-box;
  min-height: 50px;
  align-items: center;
  position: absolute;
  top: 40px;
  left: 0;
  right: 0;
  border-radius: 5px;
  border: 1px solid ${colors('grey', 200)};
  box-shadow:
    0 3px 10px rgba(0, 0, 0, 0.02),
    0 -3px 10px rgba(0, 0, 0, 0.02);
`;

const Handler = styled.button.attrs({ type: 'button' })<{ opened: boolean; showSelection: boolean }>`
  all: unset;
  display: flex;
  padding: ${pixelSpacing('small')};
  flex-direction: row;
  align-items: center;
  border: solid 1px ${({ showSelection }) => (showSelection ? colors('black') : colors('grey', 'light'))};
  outline: ${({ showSelection, opened }) =>
    showSelection ? css`solid 1.5px ${colors(opened ? 'grey' : 'black')}` : 'none'};
  outline-offset: -1px;
  border-radius: ${pixelSpacing('xSmall')};
  gap: ${pixelSpacing('xSmall')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background-color: ${({ opened, disabled }) => {
    if (disabled) {
      return colors('grey', 'lighter');
    }
    if (opened) {
      return colors('grey');
    }
    return colors('white');
  }};
  transition: all 0.1s linear;
  > svg {
    margin-right: ${pixelSpacing()};
  }
  &:hover:not([disabled]) {
    background-color: ${colors('grey', 'dark')};
    border-color: ${colors('grey', 'dark')};
  }
`;

const TitleContainer = styled.div<{ titleColor: string }>`
  display: flex;
  flex: 1;
  flex-direction: row;
  position: relative;
  color: ${({ titleColor }) => titleColor};
  box-sizing: border-box;
  margin-left: ${pixelSpacing('small')};
`;

const Title = styled.span`
  font-size: ${pixelSize()};
`;

const RightIconContainer = styled.div<{
  isOpen: boolean;
}>`
  transition: transform 0.1s linear 0s;
  transform: ${({ isOpen }) => (isOpen ? `rotate(180deg)` : null)};
  display: flex;
  align-items: center;
  height: 15px;
`;

const Selection = styled.span`
  font-size: ${pixelSize()};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-weight: bold;
`;

type SliderPopupProps = {
  getTitle: (value?: SliderValue) => string;
  min: number;
  max: number;
  value: SliderValue;
  onValueChange: (value: SliderValue) => void;
  disabled?: boolean;
};

export function SliderPopup({ getTitle, min, max, value, onValueChange, disabled = false }: SliderPopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [showSelectionLocal, setShowSelection] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  const showSelection = showSelectionLocal && !disabled;

  useEffect(() => {
    setLocalValue((currentValue) => {
      let { start, end } = currentValue;
      if ((start || 0) < min || (start || 0) > max) {
        start = min;
      }
      if (end > max || end < min) {
        end = max;
      }
      return { start, end };
    });
  }, [max, min]);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (!showSelection) {
      onValueChange({ start: min, end: max });
    }
  }, [max, min, onValueChange, showSelection]);

  const setAndFormatValue = useCallback(
    (rangeNeedsToBeSet: boolean) => {
      return ({ start, end }: SliderValue) => {
        const { start: currentStart, end: currentEnd } = value;
        let roundStart = Math.round(start || 0);
        let roundEnd = Math.round(end);

        if (roundStart === roundEnd) {
          if (roundEnd === max) {
            roundStart = roundStart - 1;
          } else {
            roundEnd = roundEnd + 1;
          }
        }
        const range = { start: roundStart, end: roundEnd };
        setLocalValue(range);
        if (range.start !== currentStart || range.end !== currentEnd) {
          setShowSelection(true);
        }
        if (range.start <= min && range.end >= max) {
          setShowSelection(false);
        }
        if (rangeNeedsToBeSet) {
          onValueChange(range);
        }
      };
    },
    [max, min, onValueChange, value],
  );

  const titleColor = useMemo(() => {
    if (disabled) {
      return colors('grey', 'dark');
    }
    if (isOpen || isHover) {
      return colors('white', 500);
    }
    if (showSelection) {
      return colors('black');
    }
    return colors('black');
  }, [disabled, isHover, isOpen, showSelection]);

  const containerRef = useRef<HTMLDivElement>(null);
  const handleDomClick = useCallback(
    (e: MouseEvent) => {
      if (!containerRef.current || (e.target instanceof Node && !containerRef.current.contains(e.target))) {
        setIsOpen(false);
        document.removeEventListener('click', handleDomClick);
      } else {
        setIsOpen(true);
      }
    },
    [containerRef, setIsOpen],
  );

  const handlerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isOpen) {
        document.removeEventListener('click', handleDomClick);
      } else {
        document.addEventListener('click', handleDomClick);
      }
      setIsOpen((v) => !v);
    },
    [handleDomClick, isOpen, setIsOpen],
  );

  return (
    <AreaContainer
      ref={containerRef}
      onMouseOver={() => !disabled && setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <Handler
        onClick={handlerClick}
        opened={isOpen || isHover}
        showSelection={showSelection}
        disabled={disabled}
        id="area-handler"
      >
        <AreaIcon size={16} color={titleColor} />
        <TitleContainer titleColor={titleColor}>
          {showSelection ? <Selection>{getTitle(localValue)}</Selection> : <Title>{getTitle()}</Title>}
        </TitleContainer>
        <RightIconContainer isOpen={isOpen}>
          <ChevronIcon size={18} color={titleColor} />
        </RightIconContainer>
      </Handler>
      <SliderContainer isOpen={isOpen}>
        <Slider
          min={min}
          max={max}
          value={localValue}
          onChange={setAndFormatValue(false)}
          onDragEnd={setAndFormatValue(true)}
          format={String}
        />
      </SliderContainer>
    </AreaContainer>
  );
}
